import {
  getIsZipOnlySignedInUser,
  getUser,
  getUserZip,
} from '@/features/account/services/User/utils'
import { getGuestStoreParams } from '@/features/shared/utils/dataFetching/guestStoreParams'
import { type User } from '@/features/account/services/User/types'
import { getQueryClient } from '@/features/shared/utils/dataFetching/reactQuery/SegwayQueryClient'
import { type StoreParams } from '@/features/shared/utils/dataFetching/storeParams'
import { getGuestParamsFromCache } from '@/features/account/services/GuestUser/getGuestParamsFromCache'
import { type GuestStore } from '@/features/shop/services/ShoppingStore/types'
import { inferredZipQueryOptions } from '@/features/account/services/Zip/queryOptions'
import { zipUserStoreQueryOptions } from '@/features/shop/services/ShoppingStore/queryOptions'
import { type GuestUserParams } from '@/features/account/services/GuestUser/types'

type ZipOnlyUserSource = {
  zipUserStore: GuestStore | null | undefined
  inferredZip: string | null | undefined
}

/**
 * @param zipOnlyUserSource values for a zip only signed in user. omit it only in
 * client only code since the default reads the query cache, which is empty
 * during SSR and causes hydration mismatches. rendering code should pass the
 * server seeded values instead (see useLegacyStoreParams as an example).
 */
export const getStoreParams = (
  user: User,
  zipOnlyUserSource: ZipOnlyUserSource | undefined,
): StoreParams => {
  const { id, metro_id, store_id, store_location_id } = user
  if (getIsZipOnlySignedInUser(user)) {
    const queryClient = getQueryClient()
    const { zipUserStore, inferredZip } = zipOnlyUserSource ?? {
      zipUserStore: queryClient.getQueryData(zipUserStoreQueryOptions.queryKey),
      inferredZip: queryClient.getQueryData(inferredZipQueryOptions.queryKey),
    }
    return {
      user_id: id,
      metro_id: zipUserStore?.metro_id ?? null,
      store_id: zipUserStore?.id ?? null,
      store_location_id: zipUserStore?.store_location_id ?? null,
      zip: inferredZip ?? '',
    }
  }
  return {
    user_id: id,
    metro_id,
    store_id,
    store_location_id,
    zip: getUserZip(user),
  }
}

// the pure body of useLegacyStoreParams, so server code can derive
// the same store params from the same session data.
export const getLegacyStoreParams = ({
  user,
  guestParams,
  zipUserStore,
  inferredZip,
}: ZipOnlyUserSource & {
  user: User | undefined
  guestParams: GuestUserParams | undefined
}): StoreParams =>
  user?.id
    ? getStoreParams(user, { zipUserStore, inferredZip })
    : getGuestStoreParams({
        guestStore: guestParams?.guestStore,
        guestAddress: guestParams?.guestAddress,
      })

export const getStoreParamsClient = (): StoreParams => {
  const user = getUser(getQueryClient())
  // guest params come from the cache (httpOnly cookie)
  return user?.id ? getStoreParams(user, undefined) : getGuestStoreParams(getGuestParamsFromCache())
}
